import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";

interface EditProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId?: string | null;
}

interface ProgramFormData {
  code: string;
  name: string;
  shortName: string;
  perspective: string;
  website: string;
  description: string;
  isActive: boolean;
}

const EditProgramModal: React.FC<EditProgramModalProps> = ({
  isOpen,
  onClose,
  programId,
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ProgramFormData>({
    code: "",
    name: "",
    shortName: "",
    perspective: "",
    website: "",
    description: "",
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Fetch program data if editing
  const { data: programData, isLoading: isLoadingProgram } = useQuery({
    queryKey: ["grants-program", programId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/programs/${programId}`);
      return data;
    },
    enabled: !!programId && isOpen,
  });

  // Populate form when program data is loaded
  useEffect(() => {
    if (programData) {
      setFormData({
        code: programData.code || "",
        name: programData.name || "",
        shortName: programData.shortName || "",
        perspective: programData.perspective || "",
        website: programData.website || "",
        description: programData.description || "",
        isActive: programData.isActive ?? true,
      });
    }
  }, [programData]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post("/grants/programs", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-programs"] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.put(`/grants/programs/${programId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-programs"] });
      queryClient.invalidateQueries({ queryKey: ["grants-program", programId] });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      if (error.response?.status >= 500) {
        setGlobalError('Blad serwera. Sprobuj ponownie pozniej.');
      } else if (!error.response) {
        setGlobalError('Blad polaczenia. Sprawdz polaczenie z internetem.');
      } else if (error.response?.data?.details) {
        const newErrors: Record<string, string> = {};
        error.response.data.details.forEach(
          (detail: { field: string; message: string }) => {
            newErrors[detail.field] = detail.message;
          }
        );
        setErrors(newErrors);
      } else {
        setGlobalError('Wystapil nieoczekiwany blad. Sprobuj ponownie.');
      }
    },
  });

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      shortName: "",
      perspective: "",
      website: "",
      description: "",
      isActive: true,
    });
    setErrors({});
    setGlobalError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    const payload = {
      code: formData.code,
      name: formData.name,
      shortName: formData.shortName || null,
      perspective: formData.perspective || null,
      website: formData.website || null,
      description: formData.description || null,
      isActive: formData.isActive,
    };

    if (programId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending && !updateMutation.isPending) {
        resetForm();
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, createMutation.isPending, updateMutation.isPending]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingProgram) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingProgram]);

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {programId ? "Edytuj program" : "Dodaj nowy program"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingProgram ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ładowanie...</p>
            </div>
          ) : (
            <>
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Kod <span className="text-red-600">*</span>
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.code ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="FERS"
                  maxLength={20}
                  required
                  disabled={!!programId} // Disable code editing for existing programs
                />
                {errors.code && (
                  <p className="mt-1 text-xs text-red-600">{errors.code}</p>
                )}
                <p className="mt-1 text-xs text-neutral-500">
                  Unikalny kod programu (np. FERS, KFS)
                </p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nazwa <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="Fundusze Europejskie dla Rozwoju Społecznego"
                  maxLength={255}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Short Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Skrócona nazwa
                </label>
                <input
                  type="text"
                  value={formData.shortName}
                  onChange={(e) =>
                    setFormData({ ...formData, shortName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="FERS"
                  maxLength={50}
                />
              </div>

              {/* Perspective */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Perspektywa
                </label>
                <input
                  type="text"
                  value={formData.perspective}
                  onChange={(e) =>
                    setFormData({ ...formData, perspective: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="2021-2027"
                  maxLength={20}
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Np. "2021-2027" dla programów UE (opcjonalne dla KFS)
                </p>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Strona WWW
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.website ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="https://fers.parp.gov.pl"
                  maxLength={500}
                />
                {errors.website && (
                  <p className="mt-1 text-xs text-red-600">{errors.website}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Opis
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  placeholder="Opis programu..."
                  maxLength={2000}
                />
              </div>

              {/* Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Program aktywny
                </label>
              </div>
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingProgram}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : programId
              ? "Zaktualizuj"
              : "Dodaj program"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProgramModal;
